<template>
  <div class="p-4 sm:p-6">
    <div class="mb-6 sm:mb-8">
      <h1 class="text-2xl font-bold text-gray-900 dark:text-white">Tire Approvals</h1>
    </div>

    <div class="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 sm:gap-6 mb-6 sm:mb-8">
      <div class="bg-white dark:bg-black rounded-lg shadow-lg p-6 border border-gray-200 dark:border-gray-700">
        <div class="flex items-center justify-between">
          <div>
            <p class="text-sm font-medium text-gray-600 dark:text-gray-400">Total Tires</p>
            <p class="text-3xl font-bold text-gray-900 dark:text-white mt-2">
              {{ pendingCount + approvedCount + rejectedCount || 0 }}
            </p>
          </div>
          <div class="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center">
            <svg class="w-6 h-6 text-blue-600 dark:text-blue-400" fill="currentColor" viewBox="0 0 20 20">
              <path d="M10 2a8 8 0 100 16 8 8 0 000-16zM8 9a1 1 0 112 0v4a1 1 0 11-2 0V9zm1-4a1 1 0 100 2 1 1 0 000-2z" />
            </svg>
          </div>
        </div>
      </div>

      <div class="bg-white dark:bg-black rounded-lg shadow-lg p-6 border border-gray-200 dark:border-gray-700">
        <div class="flex items-center justify-between">
          <div>
            <p class="text-sm font-medium text-gray-600 dark:text-gray-400">Pending</p>
            <p class="text-3xl font-bold text-yellow-600 dark:text-yellow-400 mt-2">
              {{ pendingCount }}
            </p>
          </div>
          <div class="w-12 h-12 bg-yellow-100 dark:bg-yellow-900/30 rounded-full flex items-center justify-center">
            <svg class="w-6 h-6 text-yellow-600 dark:text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clip-rule="evenodd" />
            </svg>
          </div>
        </div>
      </div>

      <div class="bg-white dark:bg-black rounded-lg shadow-lg p-6 border border-gray-200 dark:border-gray-700">
        <div class="flex items-center justify-between">
          <div>
            <p class="text-sm font-medium text-gray-600 dark:text-gray-400">Approved</p>
            <p class="text-3xl font-bold text-green-600 dark:text-green-400 mt-2">
              {{ approvedCount }}
            </p>
          </div>
          <div class="w-12 h-12 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
            <svg class="w-6 h-6 text-green-600 dark:text-green-400" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
            </svg>
          </div>
        </div>
      </div>

      <div class="bg-white dark:bg-black rounded-lg shadow-lg p-6 border border-gray-200 dark:border-gray-700">
        <div class="flex items-center justify-between">
          <div>
            <p class="text-sm font-medium text-gray-600 dark:text-gray-400">Rejected</p>
            <p class="text-3xl font-bold text-red-600 dark:text-red-400 mt-2">
              {{ rejectedCount }}
            </p>
          </div>
          <div class="w-12 h-12 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center">
            <svg class="w-6 h-6 text-red-600 dark:text-red-400" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
            </svg>
          </div>
        </div>
      </div>
    </div>

    <TireApprovalFilters
      v-model:search="searchQuery"
      v-model:status="statusFilter"
      v-model:brand="brandFilter"
      @apply="applyFilters"
      @reset="resetFilters"
      @export="handleExport"
    />

    <div class="bg-white dark:bg-black rounded-lg shadow-lg overflow-hidden border border-gray-200 dark:border-gray-700">

      <div v-if="loading">
        <SkeletonLoader type="table" :rows="5" :columns="9" />

        <SkeletonLoader type="card" :rows="5" />
      </div>

      <div v-else-if="error" class="text-center py-16 px-6">
        <svg xmlns="http://www.w3.org/2000/svg" class="mx-auto h-16 w-16 text-red-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <h3 class="text-lg font-medium text-gray-900 dark:text-white mb-2">Error loading tires</h3>
        <p class="text-red-500 dark:text-red-400 mb-6 max-w-sm mx-auto">{{ error }}</p>
        <button
          class="bg-primary hover:bg-primary-hover text-white px-6 py-3 rounded-lg transition-colors duration-200 shadow-sm"
          @click="tireStore.fetchTires()"
        >
          Try Again
        </button>
      </div>

      <div v-else-if="tires.length > 0">
        <TireApprovalTable
          :tires="tires"
          :loading="loading"
          @view="viewTire"
          @edit="openEditModal"
          @approve="openApproveModal"
          @reject="openRejectModal"
          @delete="openDeleteModal"
        />
      </div>

      <div
        v-else
        class="text-center py-16 px-6"
      >
        <div
          class="w-20 h-20 sm:w-24 sm:h-24 mx-auto mb-6 rounded-full flex items-center justify-center"
          :style="{ backgroundColor: brandColors.primary + '20' }"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-10 w-10 sm:h-12 sm:w-12"
            :style="{ color: brandColors.primary }"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            stroke-width="2"
          >
            <path stroke-linecap="round" stroke-linejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        </div>
        <h3 class="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-3">
          {{ searchQuery || statusFilter || brandFilter ? 'No Tires Match Your Search' : 'No Tires Found' }}
        </h3>
        <p class="text-sm sm:text-base text-gray-600 dark:text-gray-400 mb-6 max-w-sm mx-auto">
          {{ searchQuery || statusFilter || brandFilter ? 'Try adjusting your filters to find what you\'re looking for.' : 'No tire registrations have been submitted yet.' }}
        </p>
      </div>

      <div v-if="tires.length > 0">
        <CustomerPagination
          :current-page="pagination.current_page"
          :last-page="pagination.last_page"
          :per-page="pagination.per_page"
          :total="pagination.total"
          @prev="goToPreviousPage"
          @next="goToNextPage"
          @goto="goToPage"
        />
      </div>

    </div>
  </div>

  <TireDetailsModal
    :is-open="showDetailsModal"
    :tire="selectedTire"
    @close="closeDetailsModal"
  />

  <EditAdminTireModal
    :is-open="showEditModal"
    :tire="tireToEdit"
    :loading="loading"
    @close="closeEditModal"
    @save="handleEditTire"
    @editVehicle="openEditVehicleModal"
  />

  <AdminEditVehicleModal
    :is-open="showEditVehicleModal"
    :vehicle="vehicleToEdit"
    @close="closeEditVehicleModal"
    @saved="handleVehicleSaved"
  />

  <ApproveTireModal
    :is-open="showApproveModal"
    :tire="tireToApprove"
    :loading="loading"
    @close="closeApproveModal"
    @confirm="confirmApprove"
  />

  <RejectTireModal
    :is-open="showRejectModal"
    :tire="tireToReject"
    :loading="loading"
    @close="closeRejectModal"
    @confirm="confirmReject"
  />

  <DeleteTireModal
    :is-open="showDeleteModal"
    :tire="tireToDelete"
    :loading="loading"
    @close="closeDeleteModal"
    @confirm="confirmDelete"
  />
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { useToast } from 'vue-toastification';
import { useAdminTireStore } from '@/stores/adminTireStore';
import { useAuthStore } from '@/stores/auth';
import { useBrand } from '@/composables/brand/useBrand';
import type { Tire } from '@/types/tire';
import type { Vehicle } from '@/types/vehicle';
import TireApprovalFilters from '@/components/admin/TireApprovalFilters.vue';
import TireApprovalTable from '@/components/admin/TireApprovalTable.vue';
import CustomerPagination from '@/components/admin/CustomerPagination.vue';
import SkeletonLoader from '@/components/common/SkeletonLoader.vue';
import TireDetailsModal from '@/components/admin/TireDetailsModal.vue';
import ApproveTireModal from '@/components/admin/ApproveTireModal.vue';
import RejectTireModal from '@/components/admin/RejectTireModal.vue';
import DeleteTireModal from '@/components/admin/DeleteTireModal.vue';
import EditAdminTireModal from '@/components/admin/EditAdminTireModal.vue';
import AdminEditVehicleModal from '@/components/admin/AdminEditVehicleModal.vue';

const toast = useToast();
const tireStore = useAdminTireStore();
const { brandColors } = useBrand();
const authStore = useAuthStore();

const searchQuery = ref('');
const statusFilter = ref('');
const brandFilter = ref(String(authStore.adminBrandId ?? ''));
const showDetailsModal = ref(false);
const showEditModal = ref(false);
const showApproveModal = ref(false);
const showRejectModal = ref(false);
const showDeleteModal = ref(false);
const showEditVehicleModal = ref(false);
const selectedTire = ref<Tire | null>(null);
const tireToEdit = ref<Tire | null>(null);
const tireToApprove = ref<Tire | null>(null);
const tireToReject = ref<Tire | null>(null);
const tireToDelete = ref<Tire | null>(null);
const vehicleToEdit = ref<Vehicle | null>(null);

const tires = computed(() => tireStore.tires);
const loading = computed(() => tireStore.loading);
const error = computed(() => tireStore.error);
const pagination = computed(() => tireStore.pagination);
const pendingCount = computed(() => tireStore.pendingCount);
const approvedCount = computed(() => tireStore.approvedCount);
const rejectedCount = computed(() => tireStore.rejectedCount);

function applyFilters() {
  tireStore.setFilters({
    search: searchQuery.value,
    status: statusFilter.value,
    brand: brandFilter.value ? Number(brandFilter.value) : undefined,
    page: 1,
    per_page: 10
  });
  tireStore.fetchTires();
}

function resetFilters() {
  searchQuery.value = '';
  statusFilter.value = '';
  brandFilter.value = String(authStore.adminBrandId ?? '');
  tireStore.resetFilters();
  tireStore.setFilters({ brand: authStore.adminBrandId });
  tireStore.fetchTires();
}

function goToPreviousPage() {
  if (pagination.value.current_page > 1) {
    tireStore.setFilters({ page: pagination.value.current_page - 1 });
    tireStore.fetchTires();
  }
}

function goToNextPage() {
  if (pagination.value.current_page < pagination.value.last_page) {
    tireStore.setFilters({ page: pagination.value.current_page + 1 });
    tireStore.fetchTires();
  }
}

function goToPage(page: number) {
  tireStore.setFilters({ page });
  tireStore.fetchTires();
}

function viewTire(tire: Tire) {
  selectedTire.value = tire;
  showDetailsModal.value = true;
}

function closeDetailsModal() {
  showDetailsModal.value = false;
  selectedTire.value = null;
}

function openEditModal(tire: Tire) {
  tireToEdit.value = tire;
  showEditModal.value = true;
}

function closeEditModal() {
  showEditModal.value = false;
  tireToEdit.value = null;
}

function openEditVehicleModal(vehicle: Vehicle | undefined) {
  if (!vehicle) return;
  vehicleToEdit.value = vehicle;
  showEditVehicleModal.value = true;
}

function closeEditVehicleModal() {
  showEditVehicleModal.value = false;
  vehicleToEdit.value = null;
}

function handleVehicleSaved(updatedVehicle: Vehicle) {
  if (tireToEdit.value && updatedVehicle) {
    tireToEdit.value.vehicle = updatedVehicle;
  }
  closeEditVehicleModal();
  tireStore.fetchTires();
}

async function handleEditTire(
  data: { pattern: string; tire_size: string; dot: string; tire_position: string; shop: string; code: string; invoice_number: string },
  newTirePhotos: File[],
  deletedTirePhotoIds: number[],
  newReceiptPhotos: File[],
  deletedReceiptPhotoIds: number[]
) {
  if (!tireToEdit.value) return;

  try {
    await tireStore.updateTire(tireToEdit.value.tire_id, data, newTirePhotos, deletedTirePhotoIds, newReceiptPhotos, deletedReceiptPhotoIds);
    toast.success('Tire updated successfully');
    closeEditModal();
  } catch (error: any) {
    toast.error(error.response?.data?.message || 'Failed to update tire');
  }
}

function openApproveModal(tire: Tire) {
  tireToApprove.value = tire;
  showApproveModal.value = true;
}

function closeApproveModal() {
  showApproveModal.value = false;
  tireToApprove.value = null;
}

async function confirmApprove() {
  if (!tireToApprove.value) return;

  try {
    await tireStore.approveTire(tireToApprove.value.tire_id);
    toast.success('Tire approved successfully');
    closeApproveModal();
  } catch (err) {
    toast.error('Failed to approve tire');
  }
}

function openRejectModal(tire: Tire) {
  tireToReject.value = tire;
  showRejectModal.value = true;
}

function closeRejectModal() {
  showRejectModal.value = false;
  tireToReject.value = null;
}

async function confirmReject(rejectionReason: string) {
  if (!tireToReject.value) return;

  try {
    await tireStore.rejectTire(tireToReject.value.tire_id, rejectionReason);
    toast.success('Tire rejected');
    closeRejectModal();
  } catch (err) {
    toast.error('Failed to reject tire');
  }
}

function openDeleteModal(tire: Tire) {
  tireToDelete.value = tire;
  showDeleteModal.value = true;
}

function closeDeleteModal() {
  showDeleteModal.value = false;
  tireToDelete.value = null;
}

async function confirmDelete() {
  if (!tireToDelete.value) return;

  try {
    await tireStore.deleteTire(tireToDelete.value.tire_id);
    toast.success('Tire deleted successfully');
    closeDeleteModal();
  } catch (err) {
    toast.error('Failed to delete tire');
  }
}

async function handleExport() {
  try {
    await tireStore.exportTires();
  } catch (error: any) {
    if (error.message === 'No tires to export with current filters') {
      toast.warning('No tires to export with current filters');
    } else {
      toast.error('Failed to export tires. Please try again.');
    }
  }
}

watch(
  () => authStore.adminBrandId,
  (newBrandId) => {
    brandFilter.value = String(newBrandId ?? '');
    tireStore.resetFilters();
    tireStore.setFilters({ brand: newBrandId });
    tireStore.fetchTires();
  },
  { immediate: true }
);
</script>
